import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ShoppingCart,
  Email,
  Image,
  TrendingUp,
  TrendingDown,
  Schedule,
  CheckCircle,
  Warning,
  Web,
  Storage,
  Refresh,
  AttachMoney,
  Person,
  Description,
  CloudUpload,
  Settings
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { dashboardAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Roles
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardAPI.getAnalytics();
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: color,
              mr: 2,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
        {trend !== undefined && trend !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {trend > 0 ? (
              <TrendingUp sx={{ color: 'success.main', mr: 0.5, fontSize: 16 }} />
            ) : trend < 0 ? (
              <TrendingDown sx={{ color: 'error.main', mr: 0.5, fontSize: 16 }} />
            ) : null}
            <Typography
              variant="caption"
              color={trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'text.secondary'}
            >
              {trend > 0 ? '+' : ''}{trend}% trend
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );



  const getActivityIcon = (type) => {
    switch (type) {
      case 'order':
        return <ShoppingCart />;
      case 'submission':
        return <Email />;
      case 'file':
        return <CloudUpload />;
      default:
        return <Description />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'order':
        return '#667eea';
      case 'submission':
        return '#f093fb';
      case 'file':
        return '#4facfe';
      default:
        return '#43e97b';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'unread':
        return 'error';
      case 'completed':
      case 'delivered':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const quickActions = [
    {
      title: 'View Orders',
      description: 'Manage customer orders',
      path: '/dashboard/orders',
      color: '#667eea',
      icon: <ShoppingCart />,
    },
    {
      title: 'Check Emails',
      description: 'Review form submissions',
      path: '/dashboard/emails',
      color: '#f093fb',
      icon: <Email />,
    },
    {
      title: 'Data Schemas',
      description: 'Manage custom data schemas',
      path: '/dashboard/schemas',
      color: '#764ba2',
      icon: <Settings />,
    },
    {
      title: 'Manage Assets',
      description: 'Upload and organize files',
      path: '/dashboard/assets',
      color: '#4facfe',
      icon: <Image />,
    },
    {
      title: 'Manage Websites',
      description: 'Configure your websites',
      path: '/dashboard/websites',
      color: '#43e97b',
      icon: <Web />,
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box>
        <Alert severity="info">
          No dashboard data available
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome back, {user?.firstName}! 👋
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Here's what's happening with your account today.
          </Typography>
        </Box>
        <Tooltip title="Refresh Dashboard">
          <IconButton onClick={fetchDashboardData} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Websites"
            value={dashboardData.overview.totalWebsites}
            subtitle="Active websites"
            icon={<Web />}
            color="#43e97b"
            onClick={() => navigate('/dashboard/websites')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Data Schemas"
            value={dashboardData.overview.totalSchemas}
            subtitle={`${dashboardData.overview.activeSchemas} active, ${dashboardData.overview.totalDataRecords} records`}
            icon={<Settings />}
            color="#764ba2"
            onClick={() => navigate('/dashboard/schemas')}
          />
        </Grid>
        {/* Storage Used - Only show for admin */}
        {isAdmin && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Storage Used"
              value={dashboardData.overview.totalStorageUsed}
              subtitle={`${dashboardData.overview.storageUsagePercentage}% of ${dashboardData.overview.totalStorageQuota}`}
              icon={<Storage />}
              color="#9c27b0"
              onClick={() => navigate('/dashboard/assets')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={dashboardData.orders.total}
            subtitle={`${dashboardData.orders.pending} pending, ${dashboardData.orders.completed} completed`}
            icon={<ShoppingCart />}
            color="#667eea"
            trend={dashboardData.orders.trend}
            onClick={() => navigate('/dashboard/orders')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Form Submissions"
            value={dashboardData.submissions.total}
            subtitle={`${dashboardData.submissions.unread} unread messages`}
            icon={<Email />}
            color="#f093fb"
            trend={dashboardData.submissions.trend}
            onClick={() => navigate('/dashboard/emails')}
          />
        </Grid>
      </Grid>

      {/* Detailed Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                This Month
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShoppingCart sx={{ mr: 1, color: '#667eea' }} />
                    <Typography variant="body2">Orders</Typography>
                  </Box>
                  <Typography variant="h6" color="#667eea">
                    {dashboardData.orders.thisMonth}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Email sx={{ mr: 1, color: '#f093fb' }} />
                    <Typography variant="body2">Submissions</Typography>
                  </Box>
                  <Typography variant="h6" color="#f093fb">
                    {dashboardData.submissions.thisMonth}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Image sx={{ mr: 1, color: '#4facfe' }} />
                    <Typography variant="body2">Files</Typography>
                  </Box>
                  <Typography variant="h6" color="#4facfe">
                    {dashboardData.files.total}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Rate Limits - Only show for admin */}
        {isAdmin && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Rate Limits
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Orders (Monthly)</Typography>
                      <Typography variant="body2">
                        {dashboardData.rateLimits.orders.monthlyUsed}/{dashboardData.rateLimits.orders.monthlyLimit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(dashboardData.rateLimits.orders.monthlyUsed / dashboardData.rateLimits.orders.monthlyLimit) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Submissions (Monthly)</Typography>
                      <Typography variant="body2">
                        {dashboardData.rateLimits.submissions.monthlyUsed}/{dashboardData.rateLimits.submissions.monthlyLimit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(dashboardData.rateLimits.submissions.monthlyUsed / dashboardData.rateLimits.submissions.monthlyLimit) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Account Status
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Profile Completion</Typography>
                  <Typography variant="body2">85%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={85} sx={{ height: 6, borderRadius: 3 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  icon={<CheckCircle />}
                  label="Email Verified"
                  color="success"
                  size="small"
                />
                <Chip
                  icon={<Warning />}
                  label="Phone Not Verified"
                  color="warning"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Recent Activity
              </Typography>
              {dashboardData.recentActivity && dashboardData.recentActivity.length > 0 ? (
                <List>
                  {dashboardData.recentActivity.map((activity, index) => (
                    <React.Fragment key={`${activity.type}-${activity.id}-${index}`}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: getActivityColor(activity.type) }}>
                            {getActivityIcon(activity.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body1" fontWeight="medium">
                                {activity.title}
                              </Typography>
                              {activity.amount && (
                                <Typography variant="body2" color="primary" fontWeight="bold">
                                  {formatCurrency(activity.amount)}
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {activity.description}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  {formatTimeAgo(activity.time)}
                                </Typography>
                                {activity.website && (
                                  <Chip
                                    label={activity.website.name}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                                {activity.status && (
                                  <Chip
                                    label={activity.status}
                                    size="small"
                                    color={getStatusColor(activity.status)}
                                    sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < dashboardData.recentActivity.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="textSecondary">
                    No recent activity
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Activity will appear here as you use the platform
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {quickActions.map((action) => (
                  <Paper
                    key={action.title}
                    sx={{
                      p: 2,
                      background: `linear-gradient(135deg, ${action.color}15 0%, ${action.color}05 100%)`,
                      border: `1px solid ${action.color}20`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => navigate(action.path)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: action.color,
                          mr: 2,
                          width: 32,
                          height: 32,
                        }}
                      >
                        {action.icon}
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {action.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {action.description}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Website Summary */}
          {dashboardData.websites && dashboardData.websites.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Website Summary
                </Typography>
                <List dense>
                  {dashboardData.websites.slice(0, 3).map((website) => (
                    <ListItem key={website._id} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#43e97b', width: 32, height: 32 }}>
                          <Web fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={website.name}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={`${website.totalOrders} orders`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            <Chip
                              label={`${website.totalSubmissions} forms`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {dashboardData.websites.length > 3 && (
                  <Button
                    size="small"
                    onClick={() => navigate('/dashboard/websites')}
                    sx={{ mt: 1 }}
                  >
                    View All Websites
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;